import { Injectable } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { catchError, lastValueFrom, Observable, of, throwError } from 'rxjs';
import { RespostaQuestionarioPayload, ExcluirRespostasPayload } from '../../core/interface/questionario';
import { Protocolo } from '../../core/interface/protocolo';
import { environment } from '../../../environments/environment';
import { CreatedPerfilParticipante, ParticipanteConfirmTerm } from '../../core/interface/perfil-participante';
import {
  ApiHttpError,
  getFriendlyMessage,
  isProblemDetailsBody,
  mapHttpStatusToEnum,
  resolveProblemMessage,
} from './api-http-error.model';

export interface ApiResponse<T> {
  status: number;
  success: boolean;
  data?: T;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class HttpClientService {

  constructor(
    private httpClient: HttpClient
  ) { }

  baseUrlPerfil = `${environment.baseUrl}/${environment.apiPerfil}`;

  async getCampanha(token: string, idCampanha: number): Promise<any> {
    const url = `${this.baseUrlPerfil}/v1/Campanha?status=AP&idCampanha=${idCampanha}`;

    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpPerfil}`
    });

    return await lastValueFrom(this.request(this.httpClient.get(url, { headers, observe: 'response' })));
  }

  async getPerfilParticipante(token: string): Promise<any> {
    const url = `${this.baseUrlPerfil}/v1/PerfilParticipante/Portal`;

    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpPerfil}`
    });

    return await lastValueFrom(this.request(this.httpClient.get(url, { headers, observe: 'response' })));
  }

  async getPerfisInvestidor(token: string, idCampanha: number): Promise<any> {
    const url = `${this.baseUrlPerfil}/v1/PerfilInvestimento/Portal?idCampanha=${idCampanha}`;
    
    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpPerfil}`
    });

    return await lastValueFrom(this.request(this.httpClient.get(url, { headers, observe: 'response' })));
  }

  async getQuestionnaire(token: string, idQuestionario: number): Promise<any> {
    const url = `${this.baseUrlPerfil}/v1/Questionario/Portal?idQuestionario=${idQuestionario}`;

    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpPerfil}`
    });

    return await lastValueFrom(this.request(this.httpClient.get(url, { headers, observe: 'response' })));
  }

  async getQuestionnaireHistory(token: string, idParticipante: number): Promise<any> {
    const url = `${this.baseUrlPerfil}/v1/PerfilParticipante/Respostas?idPerfInvestPartic=${idParticipante}`;

    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpPerfil}`
    });

    return await lastValueFrom(this.request(this.httpClient.get(url, { headers, observe: 'response' })));
  }

  async saveQuestionnaire(token: string, idParticipante: number, respostas: RespostaQuestionarioPayload): Promise<any> {
    const url = `${this.baseUrlPerfil}/v1/PerfilParticipante/${idParticipante}/Respostas`

    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpPerfil}`
    });

    return await lastValueFrom(this.request(this.httpClient.post(url, respostas, { headers, observe: 'response' })));
  }

  async getQuestionnaireParticipante(token: string, idParticipante: number): Promise<any> {
    const url = `${this.baseUrlPerfil}/v1/PerfilParticipante/${idParticipante}/Respostas`;

    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpPerfil}`
    });

    return await lastValueFrom(this.request(this.httpClient.get(url, { headers, observe: 'response' })));
  }

  async deletedQuestionnaire(token: string, idParticipante: number, payload: ExcluirRespostasPayload): Promise<any> {
    const url = `${this.baseUrlPerfil}/v1/PerfilParticipante/${idParticipante}/Respostas`;

    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpPerfil}`
    });

    return await lastValueFrom(this.request(this.httpClient.delete(url, { headers, body: payload, observe: 'response' })));
  }

  async postPerfilParticipanteRefazer(token: string, participante: CreatedPerfilParticipante): Promise<any> {
    const url = `${this.baseUrlPerfil}/v1/PerfilParticipante`;  
    
    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpPerfil}`
    });

    return await lastValueFrom(this.request(this.httpClient.post(url, participante, { headers, observe: 'response' })));
  }

  async getTermoResponsabilidade(token: string): Promise<any> {
    const url = `${this.baseUrlPerfil}/v1/Termo/download-termo-perfil-investidor`;

    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpPerfil}`
    });

    return await lastValueFrom(this.request(this.httpClient.get(url, { headers, responseType: 'blob' })));
  }

  async postPerfilInvestidor(token: string, idParticipante: number, participante: ParticipanteConfirmTerm): Promise<any> {
    const url = `${this.baseUrlPerfil}/v1/PerfilParticipante/${idParticipante}/ConfirmaTermo`;  
    
    const headers = new HttpHeaders({
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.oacpPerfil}`
    });

    return await lastValueFrom(this.request(this.httpClient.post(url, participante, { headers, observe: 'response' })));
  }

  private request<T>(source: Observable<T>): Observable<T> {
    return source.pipe(catchError((error: unknown) => this.handleError(error)));
  }

  private handleError(error: unknown): Observable<never> {
    if (error instanceof ApiHttpError) {
      return throwError(() => error);
    }

    if (!(error instanceof HttpErrorResponse)) {
      return throwError(
        () =>
          new ApiHttpError(
            mapHttpStatusToEnum(0),
            error instanceof Error ? error.message : 'Unexpected HTTP error.',
          ),
      );
    }

    const status = mapHttpStatusToEnum(error.status);
    const problem = isProblemDetailsBody(error.error) ? error.error : undefined;
    const message = resolveProblemMessage(problem, getFriendlyMessage(error.status));

    return throwError(() => new ApiHttpError(status, message, problem));
  }
}
